import { Fragment } from 'react';
import fetch from 'isomorphic-unfetch';
import Head from 'next/head';
import Link from 'next/link';
import key from 'weak-key';
import dynamic from 'next/dynamic';
import { getPluggerToken, getBlocksFromQueue } from '~/utils/utils';
import BlockParser from '~/components/blockParser/blockParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import PlugMaintenance from '~/components/plugMaintenance/plugMaintenance';
import SiteSettings from '~/siteSettings';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Be on the Show
 * @category Page
 * @extends Component
 * @summary Display the Be on the Show page.
 * @return {Node} React node containing the Be on the Show page.
 */

const BeOnTheShow = props => {
  const { sitePrimaryRsid, baseUrl, data, blocks } = props;
  const pageTitle = `Browse all Topics | Share Your Story | ${ SiteSettings.siteName }`;
  const pageDesc = 'Browse all topics - Drew wants to hear! Tell us your story.';
  const path = '/share-your-story';

  if (process.env.NEXT_PUBLIC_PLUGGER_ACTIVE !== '0' && data.maintenance !== true) {
    return <>
      <Head>
        <title>{ pageTitle }</title>
        {/* Description */ }
        <meta name='description' content={ pageDesc } />
        <meta property='og:description' content={ pageDesc } />
        <meta name='twitter:description' content={ pageDesc } />
        {/* Title */ }
        <meta name='title' content={ pageTitle } />
        <meta property='og:title' content={ pageTitle } />
        <meta name='twitter:title' content={ pageTitle } />
        {/* Others */ }
        <meta property='og:url' content={ path } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <meta name="keywords" content={ SiteSettings.defaultKeywords } />
      </Head>
      <div className='site-bg'>
        <div className='constrain--bg'>
          <BlockParser blockItems={ blocks.entitySubQueueQuery.items } />
          <div className='content constrain'>
            <div className='plug__header plug__header--secondary'>
              <h2>More topics</h2>
            </div>
            <div className='plug__categories'>
              {data.categories.map((category) => (
                <Fragment key={`category-${key(category)}`}>
                  { category.plugs.length > 0 &&
                    <div className='plug__category'>
                      <div className='plug__category-header'>
                        <div className='plug__category-icon'>
                          <img
                            src='/img/drew-sig-lg.png'
                            width='60'
                            height='63'
                            alt='Share your Story'
                            loading='lazy'
                          />
                        </div>
                        <h2>{ category.name }</h2>
                      </div>
                      <ul>
                        {category.plugs.map((plug) => (
                          <li key={`plug-${key(plug)}`}>
                            <Link legacyBehavior href={ `/share-your-story/plug/${ plug.id }` }>
                              <a aria-label={`Visit ${ plug.main_headline }`}>
                                { plug.main_headline }
                              </a>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                   }
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Utag pageType='be_on_the_show' siteSection='share_your_story' data={{ sitePrimaryRsid }} />
    </>;
  }

  return(
    <PlugMaintenance/>
  );
}

export async function getServerSideProps() {
  // Get Plugs Listing
  const token = await getPluggerToken();
  let data = {};

  if(token.pluggerToken.data.token) {
    const dataRaw = await fetch(
      `${ process.env.NEXT_PUBLIC_PLUGGER_HOSTNAME }/api/1.0/categories.json`,
      {
        method: 'GET',
        withCredentials: true,
        credentials: 'include',
        headers: {
          'Authorization': `bearer ${ token.pluggerToken.data.token }`
        }
      }
    );

    data = await dataRaw.json();
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'plugs_blocks',
    limit: 100
  });

  return {
    props: {
      data,
      blocks
    }
  }
}

export default PageWrapper(BeOnTheShow);
